import React, { useMemo } from "react";
import PropTypes from "prop-types";
import ReactEcharts from "echarts-for-react";
import { Box, Card, CardContent, CircularProgress, Grid, Typography } from "@material-ui/core";
import { makeStyles, createStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) =>
  createStyles({
    card: {
      borderRadius: 8,
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      overflow: "hidden",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      border: "1px solid #e5e7eb",
    },
    content: {
      padding: theme.spacing(2),
      "&:last-child": {
        paddingBottom: theme.spacing(2),
      },
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1.5),
    },
    title: {
      fontSize: 14,
      fontWeight: 700,
      color: "#1f2937",
    },
    subtitle: {
      fontSize: 12,
      color: "#6b7280",
      whiteSpace: "nowrap",
    },
    chart: {
      width: "100%",
      height: 480,
    },
    loadingWrap: {
      height: 480,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
  })
);

const PlotTrendChart = ({ points, title, subtitle, loading }) => {
  const classes = useStyles();

  const option = useMemo(
    () => ({
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
      },
      legend: {
        top: 8,
        data: ["プロット数", "ユニークユーザー数"],
      },
      grid: {
        left: 44,
        right: 52,
        top: 60,
        bottom: 28,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: points.map(([label]) => label),
        axisLabel: {
          color: "#64748b",
        },
      },
      yAxis: [
        {
          type: "value",
          name: "プロット数",
          axisLabel: {
            color: "#64748b",
          },
        },
        {
          type: "value",
          name: "ユニークユーザー数",
          axisLabel: {
            color: "#64748b",
          },
        },
      ],
      series: [
        {
          name: "プロット数",
          type: "bar",
          data: points.map((item) => item[1]),
          barMaxWidth: 18,
          itemStyle: {
            color: "#fb923c",
          },
        },
        {
          name: "ユニークユーザー数",
          type: "line",
          yAxisIndex: 1,
          data: points.map((item) => item[2]),
          smooth: false,
          symbol: "circle",
          symbolSize: 8,
          lineStyle: {
            width: 3,
            color: "#8b5cf6",
          },
          itemStyle: {
            color: "#8b5cf6",
          },
        },
      ],
    }),
    [points]
  );

  return (
    <Card className={classes.card}>
      <CardContent className={classes.content}>
        <div className={classes.header}>
          <Typography className={classes.title}>{title}</Typography>
          <Typography className={classes.subtitle}>{subtitle}</Typography>
        </div>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            {loading ? (
              <Box className={classes.loadingWrap}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <ReactEcharts option={option} className={classes.chart} style={{ height: 480, width: "100%" }} />
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

PlotTrendChart.propTypes = {
  points: PropTypes.arrayOf(PropTypes.array).isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  loading: PropTypes.bool,
};

PlotTrendChart.defaultProps = {
  title: "プロット数 時系列",
  subtitle: "日別",
  loading: false,
};

export default PlotTrendChart;
